import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Youtube,
  Save,
  RotateCcw,
  Eye,
  Volume2,
  VolumeX,
  Video,
  Type,
  Megaphone,
  MousePointerClick,
  Palette,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  video_url: 'https://www.youtube.com/watch?v=iNO-2YAwgfI',
  title: 'Welcome to the Dominican Chamber of Commerce of Florida',
  subtitle: 'A quick moment to set the tone — then step into your new community.',
  announcement: '',
  autoplay: true,
  muted: true,
  loop: false,
  show_skip: true,
  primary_cta_text: 'Enter the Community',
  secondary_cta_text: 'Skip for now',
  background_type: 'dark',
  show_logo: true,
  logo_position: 'center',
  destination_url: '/portal/community',
};

const extractYouTubeId = (url = '') => {
  if (!url) return null;
  const trimmed = url.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return trimmed;
  const m = trimmed.match(/(?:v=|\/embed\/|\/shorts\/|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const SectionCard = ({ icon: Icon, title, description, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6">
    <div className="flex items-start gap-3 mb-4">
      <div className="w-9 h-9 rounded-lg bg-[#002249]/5 text-[#002249] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="min-w-0">
        <h3 className="text-[14px] font-bold text-[#002249]">{title}</h3>
        {description && <p className="text-[12px] text-slate-500 mt-0.5">{description}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Toggle = ({ checked, onChange, label, description, testId }) => (
  <div className="flex items-center justify-between py-2.5">
    <div className="min-w-0 pr-3">
      <p className="text-[13px] font-semibold text-slate-800">{label}</p>
      {description && <p className="text-[11.5px] text-slate-500 mt-0.5">{description}</p>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none flex-shrink-0 ${checked ? 'bg-[#002249]' : 'bg-slate-300'}`}
      data-testid={testId}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${checked ? 'left-[22px]' : 'left-0.5'}`}
      />
    </button>
  </div>
);

const FieldLabel = ({ children }) => (
  <label className="text-[11.5px] font-semibold uppercase tracking-wide text-slate-600 block mb-1.5">
    {children}
  </label>
);

const WelcomeExperienceManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULTS);
  const [initial, setInitial] = useState(DEFAULTS);

  const videoId = extractYouTubeId(form.video_url);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/welcome-settings`, { withCredentials: true });
      const merged = { ...DEFAULTS, ...res.data };
      setForm(merged);
      setInitial(merged);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load welcome settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = (key) => (val) => setForm((prev) => ({ ...prev, [key]: val }));
  const onChange = (key) => (e) => patch(key)(e.target.value);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const handleSave = async () => {
    if (form.video_url && !videoId) {
      toast.error('Invalid YouTube URL — paste a youtube.com/watch?v=... or youtu.be/... link.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/welcome-settings`, form, { withCredentials: true });
      const merged = { ...DEFAULTS, ...res.data };
      setForm(merged);
      setInitial(merged);
      toast.success('Welcome Experience saved — live for all new members.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(initial);
    toast.info('Reverted to last saved values.');
  };

  const handleDefaults = () => {
    setForm(DEFAULTS);
    toast.info('Defaults loaded — click Save to apply.');
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-welcome-experience-page">
      <div className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#002249] flex items-center gap-2">
            <Sparkles className="w-6 h-6" /> Welcome Experience
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Controls the cinematic onboarding screen shown to every new member on their first login. Updates go live instantly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDefaults}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 px-3 h-9 rounded-lg"
            data-testid="welcome-defaults-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Defaults
          </button>
          <button
            onClick={handleReset}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed px-3 h-9 rounded-lg"
            data-testid="welcome-revert-btn"
          >
            Revert
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold bg-[#E63946] hover:bg-[#d02e3b] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 h-9 rounded-lg shadow-sm u-btn"
            data-testid="welcome-save-btn"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_minmax(460px,1fr)] gap-6">
        {/* FORM */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Section 1 — Video */}
          <SectionCard icon={Video} title="Video Settings" description="Paste a YouTube link — we auto-hide branding, related videos, and annotations.">
            <div>
              <FieldLabel>YouTube URL</FieldLabel>
              <div className="relative">
                <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="url"
                  value={form.video_url}
                  onChange={onChange('video_url')}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full pl-9 pr-3 h-10 text-[13px] rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]/40"
                  data-testid="welcome-video-url"
                />
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {videoId ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                    Detected ID: {videoId}
                  </span>
                ) : form.video_url ? (
                  <span className="text-[11px] font-semibold text-rose-600">Invalid YouTube URL</span>
                ) : null}
              </div>
            </div>
            <div className="mt-3 border-t border-slate-100 pt-2">
              <Toggle
                checked={!!form.autoplay}
                onChange={patch('autoplay')}
                label="Autoplay"
                description="Start playing the moment the page loads."
                testId="toggle-autoplay"
              />
              <Toggle
                checked={!!form.muted}
                onChange={patch('muted')}
                label="Muted Start"
                description="Required by browsers for autoplay to work. Users can unmute."
                testId="toggle-muted"
              />
              <Toggle
                checked={!!form.loop}
                onChange={patch('loop')}
                label="Loop Video"
                description="Restart the video when it ends (auto-continue disabled)."
                testId="toggle-loop"
              />
            </div>
          </SectionCard>

          {/* Section 2 — Content */}
          <SectionCard icon={Type} title="Content" description="Headline, subtitle, and optional announcement shown above the video.">
            <div className="space-y-3">
              <div>
                <FieldLabel>Title (H1)</FieldLabel>
                <input
                  type="text"
                  value={form.title}
                  onChange={onChange('title')}
                  className="w-full px-3 h-10 text-[13px] rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]/40"
                  data-testid="welcome-title-input"
                />
              </div>
              <div>
                <FieldLabel>Subtitle / Note</FieldLabel>
                <textarea
                  rows={2}
                  value={form.subtitle}
                  onChange={onChange('subtitle')}
                  className="w-full px-3 py-2 text-[13px] rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]/40 resize-none"
                  data-testid="welcome-subtitle-input"
                />
              </div>
              <div>
                <FieldLabel>Announcement (optional)</FieldLabel>
                <textarea
                  rows={2}
                  value={form.announcement}
                  onChange={onChange('announcement')}
                  placeholder="Upcoming events, onboarding instructions, etc."
                  className="w-full px-3 py-2 text-[13px] rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]/40 resize-none"
                  data-testid="welcome-announcement-input"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Appears as a pill below the subtitle. Leave blank to hide.
                </p>
              </div>
            </div>
          </SectionCard>

          {/* Section 3 — CTAs */}
          <SectionCard icon={MousePointerClick} title="CTA Buttons" description="Primary action + optional skip button.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <FieldLabel>Primary Button Text</FieldLabel>
                <input
                  type="text"
                  value={form.primary_cta_text}
                  onChange={onChange('primary_cta_text')}
                  className="w-full px-3 h-10 text-[13px] rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
                  data-testid="welcome-primary-cta"
                />
              </div>
              <div>
                <FieldLabel>Skip Button Text</FieldLabel>
                <input
                  type="text"
                  value={form.secondary_cta_text}
                  onChange={onChange('secondary_cta_text')}
                  disabled={!form.show_skip}
                  className="w-full px-3 h-10 text-[13px] rounded-lg border border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
                  data-testid="welcome-secondary-cta"
                />
              </div>
            </div>
            <div className="border-t border-slate-100 pt-2 mt-3">
              <Toggle
                checked={!!form.show_skip}
                onChange={patch('show_skip')}
                label="Show Skip Button"
                description="Let users bypass the video. Recommended ON."
                testId="toggle-show-skip"
              />
            </div>
            <div>
              <FieldLabel>Destination (after Enter / video end)</FieldLabel>
              <input
                type="text"
                value={form.destination_url}
                onChange={onChange('destination_url')}
                placeholder="/portal/community"
                className="w-full px-3 h-10 text-[13px] rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
                data-testid="welcome-destination"
              />
            </div>
          </SectionCard>

          {/* Section 4 — Design */}
          <SectionCard icon={Palette} title="Design" description="Background and logo placement.">
            <div>
              <FieldLabel>Background</FieldLabel>
              <div className="grid grid-cols-2 gap-2">
                {['dark', 'gradient'].map((bg) => (
                  <button
                    key={bg}
                    type="button"
                    onClick={() => patch('background_type')(bg)}
                    className={`h-10 rounded-lg border text-[12.5px] font-semibold capitalize u-btn ${
                      form.background_type === bg
                        ? 'bg-[#002249] text-white border-[#002249]'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-[#002249]/40'
                    }`}
                    data-testid={`bg-${bg}`}
                  >
                    {bg}
                  </button>
                ))}
              </div>
            </div>
            <div className="border-t border-slate-100 pt-2 mt-3">
              <Toggle
                checked={!!form.show_logo}
                onChange={patch('show_logo')}
                label="Show DCCF Logo"
                description="Displays the chamber logo above the title."
                testId="toggle-show-logo"
              />
            </div>
          </SectionCard>

          {/* Section 5 — Announcement quick-add (cross-ref to Section 2 for visibility) */}
          <SectionCard icon={Megaphone} title="Delivery" description="Who sees this experience, and when.">
            <div className="text-[12.5px] text-slate-600 leading-relaxed space-y-1">
              <p><strong className="text-[#002249]">Shown once</strong> to every member on their first successful login (after activation or first password change).</p>
              <p>The flag <code className="px-1 py-0.5 rounded bg-slate-100 text-[11px]">is_first_login</code> is cleared when a user clicks Enter the Community or Skip, so no one sees it twice.</p>
              <p>To re-trigger for a specific user, a super-admin can flip their flag back to true in <em>Users → Manage Access</em> (coming soon).</p>
            </div>
          </SectionCard>
        </motion.div>

        {/* LIVE PREVIEW */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[13px] font-bold text-[#002249]">
            <Eye className="w-4 h-4" /> Live Preview
            <span className="ml-auto text-[11px] font-semibold text-slate-400">Updates as you type</span>
          </div>
          <WelcomePreview settings={form} videoId={videoId} />
          <p className="text-[11px] text-slate-400 px-1">
            Preview is rendered inline with safe sample data. The real page is <code className="text-[#002249]">/welcome</code>.
          </p>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-sm z-40 flex items-center justify-center">
          <div className="text-slate-500 text-sm">Loading…</div>
        </div>
      )}
    </div>
  );
};

/* ---------- Inline preview panel ---------- */
const WelcomePreview = ({ settings, videoId }) => {
  const [previewMuted, setPreviewMuted] = useState(!!settings.muted);
  const showGradient = settings.background_type === 'gradient';
  const bgClass = showGradient
    ? 'bg-gradient-to-br from-[#001224] via-[#002249] to-[#0b1a2c]'
    : 'bg-[#001224]';

  const embedSrc = videoId
    ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=${settings.autoplay ? 1 : 0}&mute=${previewMuted ? 1 : 0}&controls=0&modestbranding=1&rel=0&iv_load_policy=3&fs=0&disablekb=1&playsinline=1&loop=${settings.loop ? 1 : 0}${settings.loop ? `&playlist=${videoId}` : ''}`
    : null;

  return (
    <div className={`relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl text-white ${bgClass}`} style={{ aspectRatio: '9 / 13', maxHeight: 720 }} data-testid="welcome-preview">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -right-20 w-[280px] h-[280px] rounded-full bg-[#002249] blur-3xl opacity-70" />
        <div className="absolute -bottom-20 -left-20 w-[240px] h-[240px] rounded-full bg-[#E63946]/20 blur-3xl" />
      </div>

      <div className="relative z-10 h-full w-full flex flex-col items-center justify-start px-4 py-5 overflow-y-auto">
        {settings.show_logo && (
          <div className="text-center mb-3">
            <div className="h-10 w-24 mx-auto rounded bg-white/10 flex items-center justify-center text-[10px] font-bold tracking-wide text-white/70">DCCF LOGO</div>
          </div>
        )}
        <p className="text-[8.5px] uppercase tracking-[0.25em] text-white/60 font-semibold text-center">
          Dominican Chamber of Commerce of Florida
        </p>
        <h1 className="text-[17px] sm:text-[20px] font-bold mt-2 tracking-tight leading-tight max-w-[420px] text-center">
          {settings.title}
        </h1>
        {settings.subtitle && (
          <p className="text-[11.5px] text-white/70 mt-1 max-w-[380px] text-center">{settings.subtitle}</p>
        )}
        {settings.announcement && (
          <div className="mt-2 inline-block text-[10.5px] text-white/85 bg-white/10 border border-white/15 px-3 py-1 rounded-full max-w-[360px] backdrop-blur-sm text-center">
            {settings.announcement}
          </div>
        )}

        {/* Video placeholder/preview */}
        <div className="w-full max-w-[420px] mt-3 aspect-video rounded-xl overflow-hidden ring-1 ring-white/10 bg-black relative">
          {embedSrc ? (
            <iframe
              key={embedSrc}
              src={embedSrc}
              title="Welcome video preview"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/40 text-[12px]">No video set</div>
          )}
          <div className="absolute top-0 inset-x-0 h-8 bg-gradient-to-b from-[#001224] via-[#001224]/85 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-[#001224] via-[#001224]/90 to-transparent pointer-events-none" />
          {videoId && (
            <button
              type="button"
              onClick={() => setPreviewMuted((m) => !m)}
              className="absolute bottom-2 right-2 h-7 px-2 rounded-full bg-white/10 backdrop-blur-md text-white border border-white/20 hover:bg-white/20 text-[10px] font-medium flex items-center gap-1"
            >
              {previewMuted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
              {previewMuted ? 'Muted' : 'Sound on'}
            </button>
          )}
        </div>

        {/* CTAs */}
        <div className="w-full max-w-[420px] mt-4 flex items-center justify-between gap-3">
          {settings.show_skip ? (
            <button type="button" className="text-[11px] text-white/60 hover:text-white/90">
              {settings.secondary_cta_text || 'Skip'}
            </button>
          ) : <span />}
          <button type="button" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#E63946] hover:bg-[#d02e3b] text-white font-semibold text-[11.5px]">
            {settings.primary_cta_text}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeExperienceManager;
